import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { PanelModule } from 'primeng/panel';

import { AgentsService } from '@generated/services/agents.service';
import { TelecomService } from '@generated/services/telecom.service';
import { CallDto } from '@generated/models/call-dto';
import { CallStatus } from '@generated/models/call-status';
import { EndReason } from '@generated/models/end-reason';
import { AgentSummary } from '@generated/models/agent-summary';
import { NotificationService } from '@core/services/notification.service';
import { BreakpointService } from '@core/services/breakpoint.service';
import { DESKTOP_ROW_HEIGHT, MOBILE_ROW_HEIGHT, LOAD_MORE_THRESHOLD } from '../conversation-list.constants';
import {
  CALL_STATUS_OPTIONS,
  TagSeverity,
  callStatusLabel,
  callStatusSeverity,
  endReasonLabel,
  endReasonSeverity,
  formatCallDuration,
} from '../call-labels';

@Component({
  selector: 'app-calls-list',
  imports: [
    DatePipe,
    FormsModule,
    RouterLink,
    ScrollingModule,
    DatePickerModule,
    SelectModule,
    TagModule,
    ProgressSpinnerModule,
    PanelModule,
  ],
  templateUrl: './calls-list.component.html',
  styleUrl: './calls-list.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CallsListComponent implements OnInit {
  private readonly agentsService = inject(AgentsService);
  private readonly telecomService = inject(TelecomService);
  private readonly notificationService = inject(NotificationService);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  /** Панель фильтров стартует свёрнутой на мобиле — иначе занимает экран целиком. */
  readonly breakpoints = inject(BreakpointService);

  readonly ROWS_PER_PAGE = 20;

  /** Высота строки для cdk-virtual-scroll — должна совпадать с CSS
   *  (.list-row height), иначе виртуальный скроллер промахивается. */
  readonly rowHeight = computed(() =>
    this.breakpoints.isMobile() ? MOBILE_ROW_HEIGHT : DESKTOP_ROW_HEIGHT
  );

  readonly dateAfter = signal<Date | null>(null);
  readonly dateBefore = signal<Date | null>(null);
  readonly selectedAgentId = signal<string | null>(null);
  readonly selectedStatus = signal<CallStatus | null>(null);

  readonly calls = signal<CallDto[]>([]);
  readonly isLoading = signal<boolean>(false);
  private nextCursor: string | null = null;
  readonly hasMore = signal<boolean>(true);

  readonly statusOptions = CALL_STATUS_OPTIONS;

  getEndReasonLabel(r: EndReason | null | undefined): string {
    return endReasonLabel(r);
  }

  getEndReasonSeverity(r: EndReason | null | undefined): TagSeverity {
    return endReasonSeverity(r);
  }

  readonly unknownAgentLabel = $localize`:@@conversations.unknownAgent:Unknown Agent`;

  readonly agents = signal<AgentSummary[]>([]);
  readonly agentOptions = computed(() => {
    const unnamed = $localize`:@@conversations.unnamedAgent:Unnamed Agent`;
    return this.agents()
      .map(a => ({ label: a.title || unnamed, value: a.id || '' }))
      .filter(o => o.value);
  });

  ngOnInit(): void {
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        if (params['agentId']) this.selectedAgentId.set(params['agentId']);
        this.loadAgents();
        this.loadMore();
      });
  }

  private loadAgents(): void {
    this.agentsService.apiAgentsGet()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => this.agents.set(result.agents || []),
        error: () => this.notificationService.showError($localize`:@@conversations.notify.agentsFail:Failed to load agents`),
      });
  }

  loadMore(): void {
    if (this.isLoading()) return;
    if (this.calls().length > 0 && !this.nextCursor) return;

    this.isLoading.set(true);
    this.telecomService.apiTelecomCallsGet({
      agentId: this.selectedAgentId() || undefined,
      status: this.selectedStatus() || undefined,
      dateFrom: this.dateFromIso,
      dateTo: this.dateToIso,
      cursor: this.nextCursor || undefined,
      limit: this.ROWS_PER_PAGE,
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.calls.update(prev => [...prev, ...(result.calls || [])]);
          this.nextCursor = result.next_cursor || null;
          this.hasMore.set(!!this.nextCursor);
          this.isLoading.set(false);
        },
        error: () => {
          this.notificationService.showError($localize`:@@conversations.notify.callsFail:Failed to load calls`);
          this.isLoading.set(false);
        },
      });
  }

  /**
   * Инфинит-скролл: cdk-virtual-scroll сообщает индекс первой видимой
   * строки, сколько строк помещается в вьюпорт — считаем из его высоты.
   * Следующую страницу запрашиваем, когда до конца загруженных данных
   * осталось меньше LOAD_MORE_THRESHOLD строк.
   */
  onScrolledIndexChange(firstVisibleIndex: number, viewportHeight: number): void {
    if (!this.nextCursor || this.isLoading()) return;
    const visibleCount = Math.ceil(viewportHeight / this.rowHeight());
    if (firstVisibleIndex + visibleCount >= this.calls().length - LOAD_MORE_THRESHOLD) {
      this.loadMore();
    }
  }

  onFilterChange(): void {
    this.calls.set([]);
    this.nextCursor = null;
    this.hasMore.set(true);
    this.loadMore();
  }

  private get dateFromIso(): string | undefined {
    const d = this.dateAfter();
    return d ? d.toISOString() : undefined;
  }

  private get dateToIso(): string | undefined {
    const d = this.dateBefore();
    if (!d) return undefined;
    const end = new Date(d);
    end.setHours(23, 59, 59, 999);
    return end.toISOString();
  }

  getStatusLabel(status: CallStatus | null | undefined): string {
    return callStatusLabel(status);
  }

  getStatusSeverity(status: CallStatus | null | undefined): TagSeverity {
    return callStatusSeverity(status);
  }

  formatDuration(seconds: number | null | undefined): string {
    return formatCallDuration(seconds);
  }

  trackById = (_index: number, call: CallDto): string | number => call?.id ?? _index;
}
